import React, { useEffect, useMemo, useState } from "react";
import API from "../services/api";
import "./ProcessDevelopment.css";

/**
 * Gauge عقربه‌ای با SVG (بدون پکیج خارجی)
 */
const RadialGauge = ({
  value,
  min = 0,
  max = 100,
  width = 260,
  height = 170,
  label = "",
}) => {
  const cx = width / 2;
  const cy = height; // مرکز دایره پایین SVG برای نیم‌دایره
  const r = Math.min(width / 2 - 10, height - 10);

  const clamp = (v) => Math.max(min, Math.min(max, v));
  const safeValue = clamp(value);

  // نگاشت مقدار به زاویه: از -180 درجه تا 0 درجه (نیم‌دایره بالا)
  const valueToAngle = (v) => {
    const t = (v - min) / (max - min || 1); // 0..1
    return -180 + t * 180; // -180..0
  };

  const angle = valueToAngle(safeValue);

  const polarToXY = (angleDeg, radius) => {
    const a = (Math.PI / 180) * angleDeg;
    return {
      x: cx + radius * Math.cos(a),
      y: cy + radius * Math.sin(a),
    };
  };

  const arcPath = () => {
    const start = polarToXY(-180, r);
    const end = polarToXY(0, r);
    // large-arc-flag=0 چون نیم دایره است
    return `M ${start.x} ${start.y} A ${r} ${r} 0 0 1 ${end.x} ${end.y}`;
  };

  const needleEnd = polarToXY(angle, r * 0.85);
  const needleBase = polarToXY(angle, r * 0.10);

  const mid = (min + max) / 2;

  const tick = (v, len = 10) => {
    const a = valueToAngle(v);
    const p1 = polarToXY(a, r);
    const p2 = polarToXY(a, r - len);
    return { p1, p2 };
  };

  const minTick = tick(min, 12);
  const midTick = tick(mid, 12);
  const maxTick = tick(max, 12);

  const minTextPos = polarToXY(valueToAngle(min), r - 28);
  const midTextPos = polarToXY(valueToAngle(mid), r - 28);
  const maxTextPos = polarToXY(valueToAngle(max), r - 28);

  return (
    <div className="gauge-svg-wrap">
      <svg width={width} height={height + 18} viewBox={`0 0 ${width} ${height + 18}`}>
        {/* Arc */}
        <path d={arcPath()} className="gauge-arc" />

        {/* Ticks */}
        <line x1={minTick.p1.x} y1={minTick.p1.y} x2={minTick.p2.x} y2={minTick.p2.y} className="gauge-tick" />
        <line x1={midTick.p1.x} y1={midTick.p1.y} x2={midTick.p2.x} y2={midTick.p2.y} className="gauge-tick" />
        <line x1={maxTick.p1.x} y1={maxTick.p1.y} x2={maxTick.p2.x} y2={maxTick.p2.y} className="gauge-tick" />

        {/* Tick labels */}
        <text x={minTextPos.x} y={minTextPos.y} className="gauge-tick-text" textAnchor="start">
          {Number(min).toFixed(0)}
        </text>
        <text x={midTextPos.x} y={midTextPos.y} className="gauge-tick-text" textAnchor="middle">
          {Number(mid).toFixed(0)}
        </text>
        <text x={maxTextPos.x} y={maxTextPos.y} className="gauge-tick-text" textAnchor="end">
          {Number(max).toFixed(0)}
        </text>

        {/* Needle */}
        <line
          x1={cx}
          y1={cy}
          x2={needleEnd.x}
          y2={needleEnd.y}
          className="gauge-needle"
        />
        {/* Needle base subtle */}
        <line
          x1={cx}
          y1={cy}
          x2={needleBase.x}
          y2={needleBase.y}
          className="gauge-needle-base"
        />

        {/* Center cap */}
        <circle cx={cx} cy={cy} r="6" className="gauge-cap" />

        {/* Value label */}
        <text x={cx} y={height + 14} className="gauge-value" textAnchor="middle">
          {label}
        </text>
      </svg>
    </div>
  );
};

/**
 * کارت نمایش تگ آنالوگ
 */
const AnalogTagCard = ({ title, tagName, latestTags, min = 0, max = 100, unit = "" }) => {
  const tagData = latestTags.find((t) => t.tag_name === tagName);

  const numericValue =
    tagData &&
    tagData.last_value !== null &&
    tagData.last_value !== undefined &&
    !isNaN(tagData.last_value)
      ? Number(tagData.last_value)
      : null;

  const valueText = numericValue !== null ? numericValue.toFixed(2) : "N/A";
  const timestamp = tagData ? tagData.timestamp : "N/A";

  // برای Gauge: اگر مقدار نداریم، وسط بازه
  const gaugeValue = numericValue !== null ? numericValue : (min + max) / 2;

  return (
    <div className="analog-card">
      <div className="analog-card-header">
        <h3>{title}</h3>
        <span className="analog-tagname">{tagName}</span>
      </div>

      <div className="analog-card-body">
        <RadialGauge
          value={gaugeValue}
          min={min}
          max={max}
          label={numericValue !== null ? `${valueText}${unit}` : "N/A"}
        />

        <div className="analog-row">
          <span className="label">Value</span>
          <span className="value">
            {valueText}
            {unit}
          </span>
        </div>

        <div className="analog-row">
          <span className="label">Timestamp</span>
          <span className="value-timestamp">{timestamp}</span>
        </div>
      </div>
    </div>
  );
};

const ProcessDevelopment = () => {
  const [latestTags, setLatestTags] = useState([]);

  const analogTags = useMemo(
    () => [
      { title: "Reactor Temperature 1", tagName: "Temperature_13_SS20_inside_1", min: 0, max:200, unit: " °C" },
      { title: "Reactor Temperature 2", tagName: "Temperature_14_SS20_inside_2", min: 0, max: 200, unit: " °C" },
      { title: "Reactor Pressure", tagName: "PT_02_Pressure_SS20", min: 0, max: 8, unit: " bar" },
      { title: "Oil In Temperature", tagName: "Temperature_5_SS20_oil_in", min: 0, max: 200, unit: " °C" },
      { title: "Oil Out Temperature", tagName: "Temperature_6_SS20_oil_out", min: 0, max: 200, unit: " °C" },
      { title: "Oil Flow Rate", tagName: "FT_02_Oil_Flow_SS20", min: 0, max: 100, unit: " L/min" },
      { title: "pH Value 1", tagName: "PH_3_SS20_1", min: 0, max: 14, unit: "" },
      { title: "pH Value 2", tagName: "PH_4_SS20_2", min: 0, max: 14, unit: "" },
        { title: "Motor Current", tagName: "CT_02_Motor_SS20", min: 0, max: 1000, unit: " ma" },
    ],
    []
  );

  const fetchLatestTags = async () => {
    try {
      const response = await API.get("/latest-tag-values/");
      setLatestTags(response.data);
    } catch (error) {
      console.error("Error fetching latest tag values:", error);
    }
  };

  useEffect(() => {
    fetchLatestTags();
    const interval = setInterval(fetchLatestTags, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="process-dev-container">
      <h1>Process Development</h1>
      <p className="subtitle">Live Analog Tags (auto refresh every 3s)</p>

      <div className="analog-grid">
        {analogTags.map((t) => (
          <AnalogTagCard
            key={t.tagName}
            title={t.title}
            tagName={t.tagName}
            latestTags={latestTags}
            min={t.min}
            max={t.max}
            unit={t.unit}
          />
        ))}
      </div>
    </div>
  );
};

export default ProcessDevelopment;
